/**
 * Shared utility for calculating grid positions from pixel coordinates
 */

/**
 * Calculate the number of columns based on screen width and chat sidebar visibility
 * @param {boolean} isChatSidebarVisible - Whether the chat sidebar is visible
 * @returns {number} Number of columns
 */
export const getGridCols = (isChatSidebarVisible = false) => {
  const width = window.innerWidth;
  const effectiveWidth = isChatSidebarVisible ? width - 400 : width;
  if (effectiveWidth <= 768) return 4;
  if (effectiveWidth <= 1200) return 8;
  return 12;
};

/**
 * Calculate grid position from pixel coordinates
 * @param {number} mouseX - Mouse X coordinate
 * @param {number} mouseY - Mouse Y coordinate
 * @param {HTMLElement} containerElement - The container element
 * @param {number} cols - Number of grid columns
 * @param {Array} layouts - Current grid layouts for occupancy checking
 * @param {boolean} isChatSidebarVisible - Whether chat sidebar is visible (for responsive calculations)
 * @returns {{x: number, y: number}} Grid position
 */
export const calculateGridPosition = (
  mouseX,
  mouseY,
  containerElement,
  layouts = null,
  isChatSidebarVisible = false
) => {
  if (!containerElement) return { x: 0, y: 0 };

  // Find the react-grid-layout container
  const gridLayoutElement =
    containerElement.querySelector(".react-grid-layout");
  if (!gridLayoutElement) return { x: 0, y: 0 };

  const gridRect = gridLayoutElement.getBoundingClientRect();
  const relativeX = mouseX - gridRect.left;
  const relativeY = mouseY - gridRect.top;

  // Get grid layout properties
  const gridCols = getGridCols(isChatSidebarVisible);
  const margin = 16;

  // Calculate approximate cell dimensions
  const containerWidth = gridRect.width;
  const colWidth = (containerWidth - (gridCols - 1) * margin) / gridCols;

  // Convert mouse position to approximate grid coordinates
  const targetX = Math.floor(Math.max(0, relativeX) / (colWidth + margin));
  const targetY = Math.floor(Math.max(0, relativeY) / 16);

  // If no layouts provided, return simple position calculation
  if (!layouts || !Array.isArray(layouts)) {
    const finalX = Math.max(0, Math.min(targetX, gridCols - 1));
    const finalY = Math.max(0, targetY);
    return { x: finalX, y: finalY };
  }

  // Build occupancy map from current layouts for collision detection
  const occupancyMap = new Map();

  layouts.forEach((item) => {
    if (item.i === "__chip_placeholder__") return; // Skip placeholder

    const x = item.x || 0;
    const y = item.y || 0;
    const w = item.w || 1;
    const h = item.h || 1;

    // Mark all cells this item occupies
    for (let row = y; row < y + h; row++) {
      for (let col = x; col < x + w; col++) {
        occupancyMap.set(`${col},${row}`, true);
      }
    }
  });

  // Function to check if a position is vacant for the given size
  const isPositionVacant = (x, y, w, h) => {
    if (x < 0 || x + w > gridCols || y < 0) return false;

    for (let row = y; row < y + h; row++) {
      for (let col = x; col < x + w; col++) {
        if (occupancyMap.has(`${col},${row}`)) {
          return false;
        }
      }
    }
    return true;
  };

  // Get the size we're trying to place (from the dragged item)
  const draggedItem = window.draggedStackItem;
  const itemW = draggedItem ? draggedItem.w || 1 : 1;
  const itemH = draggedItem ? draggedItem.h || 1 : 1;

  // Search in range around target position
  const startX = Math.max(0, targetX - Math.floor(itemW / 2));
  const endX = Math.min(gridCols - itemW, targetX + Math.floor(itemW / 2));
  const startY = Math.max(0, targetY - Math.floor(itemH / 2) - 1);
  const maxY = 100; // Reasonable upper limit for search

  // Search for first available position in the range
  for (let y = startY; y <= maxY; y++) {
    for (let x = startX; x <= endX; x++) {
      if (isPositionVacant(x, y, itemW, itemH)) {
        console.debug("Found position:", { x, y });
        return { x, y };
      }
    }
  }

  // Fallback to bottom of grid if no position found in range
  let fallbackY = 0;
  layouts.forEach((item) => {
    if (item.i !== "__chip_placeholder__") {
      const itemBottom = (item.y || 0) + (item.h || 1);
      fallbackY = Math.max(fallbackY, itemBottom);
    }
  });

  const fallbackX = Math.max(0, Math.min(targetX, gridCols - itemW));
  console.debug("Using fallback position:", { x: fallbackX, y: fallbackY });
  return { x: fallbackX, y: fallbackY };
};
